import {
  isEcommerceLeadIdError,
  isEcommerceTransmissionPath,
} from '@/api/ecommerce';
import {
  formDataQueryOptions,
  isValidSelfServiceId,
  useFormDataQuery,
} from '@/api/getters';
import { Stages } from '@/api/types';
import Devtools from '@/components/devtools/Devtools';
import { isVehicleDamageProduct } from '@/components/helpers/productHelpers';
import Layout from '@/components/layouts/Layout';
import { ERROR_MESSAGES, PRODUCT } from '@/consts';
import Ecommerce from '@/features/ecommerce/Ecommerce';
import { appLoaderHH } from '@/features/v2/AppLoaderHH';
import appLoaderVD from '@/features/v2/AppLoaderVD';
import EmailGateDialog from '@/features/v2/components/EmailGateDialog';
import HouseHold from '@/features/v2/hh/HH';
import { AdditionalInfoProvider } from '@/features/v2/lib/additionalInfo';
import { WizardProvider } from '@/features/v2/lib/wizard';
import { CaseCardVariants } from '@/features/v2/MobileMenu';
import { PRODUCT_DATA } from '@/features/v2/product-data';
import VehicleDamage from '@/features/v2/vehicle-damage/VehicleDamage';

import { useLingui } from '@lingui/react';
import * as Sentry from '@sentry/react';
import { QueryClient } from '@tanstack/react-query';
import { LoaderFunctionArgs, redirect } from 'react-router-dom';

export const AppLoaderV2 =
  (queryClient: QueryClient) => async (props: LoaderFunctionArgs) => {
    const { params, request } = props;
    const id = params.id as string;
    if (!isValidSelfServiceId(id)) {
      throw new Response(null, { status: 404 });
    }
    const isEcommerce = isEcommerceTransmissionPath(
      new URL(request.url).pathname
    );
    try {
      const query = formDataQueryOptions(id, isEcommerce);
      const data = await queryClient.ensureQueryData(query);

      if (!(data.product.id in PRODUCT_DATA)) {
        throw new Response(ERROR_MESSAGES.UNSUPPORTED_PRODUCT, { status: 404 });
      }
      if (PRODUCT_DATA[data.product.id].stages[data.stage] === undefined) {
        throw new Response(ERROR_MESSAGES.UNSUPPORTED_STAGE, { status: 404 });
      }

      if (
        data.product.id === PRODUCT.VEHICLE_DAMAGE ||
        data.product.id === PRODUCT.VEHICLE_DAMAGE_TRANSPORT
      ) {
        await appLoaderVD({ data, id, queryClient });
      }
      if (data.product.id === PRODUCT.HOUSE_HOLD) {
        await appLoaderHH({ data, id, queryClient });
      }

      if (data.product.id) return { data };
    } catch (e) {
      // Any failure in the post-lead e-commerce flow (missing lead,
      // expired hash, backend error, ...) sends the user back to the
      // lead capture form rather than showing an error page.
      if (isEcommerce) {
        throw redirect('/ecommerce/transmission');
      }

      const query = formDataQueryOptions(id, isEcommerce);

      if (e instanceof Response) {
        if (isEcommerce && (await isEcommerceLeadIdError(e))) {
          throw redirect('/ecommerce/transmission');
        }
        throw e;
      }

      if (import.meta.env.VITE_SHOW_LOGS === 'true') {
        const data = (await queryClient.ensureQueryData(query)) as unknown as {
          message: string;
        };
        throw new Response(data.message ?? ERROR_MESSAGES.API_DOWN, {
          status: 503,
        });
      }

      throw new Response(ERROR_MESSAGES.API_DOWN, {
        status: 503,
      });
    }
  };

function AppV2() {
  const { i18n } = useLingui();
  const { data } = useFormDataQuery();

  if (!data) return null;

  const showDevtools = import.meta.env.VITE_SHOW_DEV_TOOLS === 'true';

  const stageData = PRODUCT_DATA[data.product.id]?.stages[data.stage];

  if (!stageData) return null;

  const progress = stageData.progress;

  const isTransmission = data.product.id === PRODUCT.TRANSMISSION;

  return (
    <AdditionalInfoProvider data={data}>
      <WizardProvider data={data}>
        <Layout
          data={data}
          withoutProgress={
            !progress ||
            !!(
              data.stage === Stages.CONTACT_US &&
              (data?.offer?.offer_rejected === true ||
                data?.offer?.offer_rejected === false)
            )
          }
          mainClassName={
            isTransmission
              ? 'py-[22px] pb-[100px] xxs:py-[22px] xxs:pb-[100px] lg:pb-[120px] lg:pt-[30px]'
              : undefined
          }
          navbarProps={{
            caseCardVariant: isVehicleDamageProduct(data.product.id)
              ? CaseCardVariants.VEHICLE_DAMAGE
              : isTransmission
                ? CaseCardVariants.EMPTY
                : CaseCardVariants.HOUSE_HOLD,
            ...(isTransmission && {
              disableMobileMenu: true,
              compactPhoneButton: true,
              title: (
                <span className="flex items-center gap-1.5 text-sm">
                  <span className="hidden text-dark-gray-100 xs:block">
                    {i18n.t('Numer Twojego zamówienia:')}
                  </span>
                  <span className="font-semibold">{data.id}</span>
                </span>
              ),
            }),
          }}
        >
          {isVehicleDamageProduct(data.product.id) && <VehicleDamage />}

          {data.product.id === PRODUCT.HOUSE_HOLD && <HouseHold />}

          {data.product.id === PRODUCT.TRANSMISSION && <Ecommerce />}

          <EmailGateDialog />

          {showDevtools && (
            <>
              <Devtools />
            </>
          )}
        </Layout>
      </WizardProvider>
    </AdditionalInfoProvider>
  );
}

export default Sentry.withProfiler(AppV2);
